// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import { get, map, reduce, sortBy, assign, isArray, indexOf } from 'lodash';

import * as constants from '../../constants';
import { getHashingMetadata } from './responseParser';

const initialState = {
  certificatesStatus: [],
  connectionStatus: [],
  deploymentStatus: {
    results: [],
  },
  proxyErrors: [],
};

export default (state = initialState, action) => {
  switch (action.type) {
    case constants.FETCH_REQUESTS_SUCCESS:
      return {
        ...state,
        requests: action.payload,
      };
    case constants.FETCH_APP_SECRET_HASHING_METADATA_SUCCESS:
      return {
        ...state,
        appSecretHashingMetadata: getHashingMetadata(get(action, 'payload.value')),
      };
    case constants.FETCH_DEPLOYMENT_REFRESH_COUNTDOWN_IN_SECONDS_SUCCESS:
      return {
        ...state,
        deploymentRefreshTime: get(action, 'payload.value'),
      };
    case constants.FETCH_APPLICATION_REQUEST_SETTING_SUCCESS:
      {
        const { requestSettings } = state;
        return {
          ...state,
          requestSettings: {
            ...requestSettings,
            isApplicationRequestWorkflowEnabled: get(action, 'payload'),
          },
        };
      }
    case constants.FETCH_APPLICATION_EDIT_REQUEST_SETTING_SUCCESS:
      {
        const { requestSettings } = state;
        return {
          ...state,
          requestSettings: {
            ...requestSettings,
            isEditApplicationRequestWorkflowEnabled: get(action, 'payload'),
          },
        };
      }
    case constants.FETCH_APPLICATION_DELETE_REQUEST_SETTING_SUCCESS:
        {
          const { requestSettings } = state;
          return {
            ...state,
            requestSettings: {
              ...requestSettings,
              isDeleteApplicationRequestWorkflowEnabled: get(action, 'payload'),
            },
          };
        }
    case constants.HEADER_PROXY_STATUS_GET_SUCCESS:
      return {
        ...state,
        certificatesStatus: get(action, 'payload.certificatesStatus'),
        connectionStatus: get(action, 'payload.connectionStatus'),
        deploymentStatus: get(action, 'payload.deploymentStatus'),
        keyStoreStatus: get(action, 'payload.keyStoreStatus'),
        proxyErrors: [],
      };
    case constants.HEADER_PROXY_STATUS_GET_ERROR:
      return {
        ...state,
        certificatesStatus: [],
        connectionStatus: [],
        deploymentStatus: [],
        proxyErrors: get(action, 'payload'),
      };
    case constants.FETCH_SETTING_USER_LOCK_CREATION_SUCCESS:
      {
        const { featureFlags } = state;
        return {
          ...state,
          featureFlags: {
            ...featureFlags,
            lockUserCreation: action.payload,
          },
        };
      }
    case constants.FETCH_DISABLE_LOCAL_LOGIN_FEATURE_FLAG_SUCCESS:
      {
        const { featureFlags } = state;
        return {
          ...state,
          featureFlags: {
            ...featureFlags,
            disableLocalLogins: action.payload,
          },
        };
      }
    default:
      return state;
  }
};

export const getConfig = (state) => state.portalConfig && state.portalConfig.config;
export const getDict = (state) => state.portalConfig && state.portalConfig.dict;
export const getUserContexts = (state) => state.portalConfig && state.portalConfig.userContexts;
export const getActiveUserContext = (state) => getUserContexts(state).userContexts[0];
export const getUserAccessibleOrgs = (state) =>
  map(
    getUserContexts(state) && getUserContexts(state).userContexts[0].accessibleOrgs,
    (uuid, name) => ({ uuid, name }),
  );
export const getUserActiveOrgUuid = (state) =>
  getUserContexts(state) && getUserContexts(state).userContexts[0].activeOrgUuid;
export const getUserActiveOrg = (state) =>
  getUserAccessibleOrgs(state).filter(org => org.uuid === getUserActiveOrgUuid(state));
export const getUserDetails = (state) =>
  getUserContexts(state) && getUserContexts(state).userContexts[0].userDetails;
export const getCanAddAPI = (state) => {
    const user = getUserContexts(state) && getUserContexts(state).userContexts[0].userDetails;
    const permissions = get(user, 'permissions', {});
    const apiPermissions = (isArray(permissions && permissions.API) && permissions.API) || [];
    return (indexOf(apiPermissions, 'CREATE') !== -1);
  };
export const getUserPrefs = (state) => get(state, 'portalConfig.userPrefs', {});

export const getServices = (state) => state.portalConfig && state.portalConfig.services;
export const getGlobalNavigation = (state) => state.portalConfig &&
  state.portalConfig.globalNavigation;
export const getCQNavigation = (state) => state.portalConfig && state.portalConfig.cqNavigation;
export const getThemes = (state) => state.portalConfig && state.portalConfig.themes;
export const getDisplayCopyright = (state: Object) =>
  get(state, 'portalConfig.themes.display.copyright', true);
export const getHelpButtonLinkUri = (state) =>
  get(state, 'portalConfig.config.portal.helpButtonLinkUri');
export const getPublisherNavigation = (state) => state.portalConfig &&
state.portalConfig.publisherNavigation;
export const getDeveloperNavigations = (state) => state.portalConfig &&
state.portalConfig.developerNavigation;
export const getSettingsNavigation = (state) => state.portalConfig &&
state.portalConfig.settingsNavigation;

export const getPublicAuthSchemesList = (state) =>
  state.portalConfig && state.portalConfig.authSchemesPublicList;

export const getFeatureFlags = (state) => state.portalConfig && state.portalConfig.featureFlags;
export const getFeatureFlagApiPlans = (state) => (
  get(getFeatureFlags(state), 'apiPlans.value') === 'true'
);
export const getUseLegacyApiPages = (state) => (
  get(getFeatureFlags(state), 'useLegacyApiPages.value') === 'true'
);
export const getUseLegacyApplicationPages = (state) => (
  get(getFeatureFlags(state), 'useLegacyApplicationPages.value') === 'true'
);
export const getNewLandingPage = (state) => (
  get(getFeatureFlags(state), 'newLandingPage.value') === 'true'
);
export const getUseMultiApiKeys = (state) => (
  get(getFeatureFlags(state), 'useMultiApiKeys.value') === 'true'
);
export const getLockUserCreation = (state) => (
  get(getFeatureFlags(state), 'lockUserCreation.value') === 'true'
);
export const getLocalLoginsDisabled = (state) => (
  get(getFeatureFlags(state), 'disableLocalLogins.value') === 'true'
);
export const getFeatureFlagForCustomPages = (state) => (
  get(getFeatureFlags(state), 'customPages.value') === 'true'
);
export const getFeatureFlagAllowGWPApiUpdates = (state) => (
  get(getFeatureFlags(state), 'allowGWPApiUpdates.value') === 'true'
);
export const getFeatureFlagOktaIdp = (state) => (
  get(getFeatureFlags(state), 'oktaIdp.value') === 'true'
);
export const getSamlProxied = (state) => (
  get(getFeatureFlags(state), 'ahubIdp.value') === 'true' || get(getFeatureFlags(state), 'oktaIdp.value') === 'true'
);
export const getFeatureFlagOrgFilterForOrgPublishers = (state) => (
  get(getFeatureFlags(state), 'featureFlagOrgFilterForOrgPublishers.value') === 'true'
);
export const getFeatureFlagForKeyStore = state => (
  get(getFeatureFlags(state), 'featureFlagKeyStores.value') === 'true'
);
export const getFeatureFlagForRemoteKeyStore = state => (
  get(getFeatureFlags(state), 'remoteKeyStore.value') === 'true'
);

export const getFeatureFlagForDistributeRateLimit = (state) => (
  get(getFeatureFlags(state), 'distributeRateLimit.value') === 'true'
);

export const getRequests = (state) => state.portalConfig &&
       state.portalConfig.requests && state.portalConfig.requests.results;
export const getAppSecretHashingMetadata = (state) => get(state, 'portalConfig.appSecretHashingMetadata');
export const getDeploymentRefreshTime = (state) => get(state, 'portalConfig.deploymentRefreshTime');

const reducePortalAdminAuthorities = (acc: boolean, value: Object) => {
  const adminAuthorities = ['portaladministrators'];
  return (acc || adminAuthorities.includes(value.authority));
};

export const getIsPortalAdmin = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  reduce(getUserDetails(state).authorities, reducePortalAdminAuthorities, false);

const reduceApiOwnerAuthorities = (acc: boolean, value: Object) => {
  const adminAuthorities = ['apiowners'];
  return (acc || adminAuthorities.includes(value.authority));
};

export const getIsApiOwner = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  reduce(getUserDetails(state).authorities, reduceApiOwnerAuthorities, false);

const reduceOrgPublisherAuthorities = (acc: boolean, value: Object) => {
  const adminAuthorities = ['orgpublishers'];
  return (acc || adminAuthorities.includes(value.authority));
};

export const getIsOrgPublisher = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  reduce(getUserDetails(state).authorities, reduceOrgPublisherAuthorities, false);

export const getIsAnonymousRole = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  (getUserDetails(state).authorities.length === 0);

export const getCookieConsent = (state) =>
  get(state, 'portalConfig.themes.display.cookieConsent');
export const getThemeBrandedLabels = (state) =>
  get(state, 'portalConfig.themes.label');

export const getProxyStatus = (state) => {
  const connectionStatus = get(state, 'portalConfig.connectionStatus', []);
  const notConnected = connectionStatus.filter((obj) => {
    obj.count = 1;
    return (
      obj.connectionStatus === 'NOT_CONNECTED' &&
      obj.enrollmentStatus === constants.PROXY_ENROLMENT_STATUS_ACTIVE
    );
  });
  const deploymentStatus = get(state, 'portalConfig.deploymentStatus.results', []);
  const obj = {};
  deploymentStatus.forEach((item)=>{
    const type = [
      constants.PROXY_PORTAL_API_TYPE,
      constants.PROXY_GATEWAY_API_TYPE,
    ].includes(item.type) ? 'api' : item.type;
    const key = `${item.uuid}_${type}`;
    if(obj[key]) {
      obj[key].count = obj[key].count + item.count;
    } else {
      obj[key] = assign({}, item);
    }
  });
  const errorDeployment = Object.values(obj);
  const keyStoreStatus = [];
  const keyStoreStatusRaw = get(state, 'portalConfig.keyStoreStatus', []);
  keyStoreStatusRaw.forEach((item) => {
    keyStoreStatus.push({
      ...item,
      type: 'keyStore',
      count: 1,
    })
  });
  const certsStatusRaw = get(state, 'portalConfig.certificatesStatus.results', []);
  const certsStatus = [];
  certsStatusRaw.forEach((item) => {
    const { proxyName, proxyUuid, type, count } = item;
    if (count > 0) {
      certsStatus.push({
        count,
        name: proxyName,
        type,
        uuid: proxyUuid,
      });
    }
  });
  return sortBy([...notConnected,
    ...errorDeployment,...keyStoreStatus, ...certsStatus], obj => obj.uuid);
};

export const getErrors = state => get(state, 'portalConfig.proxyErrors');

export const getCmsSettings = state => get(state, 'portalConfig.cmsSettings')
